import { useState, useEffect } from "react";
import type { VisitorNotification } from "../../components/visitor/notification-types";

const MIGRATION_FLAG = "notification-migration-v1";

/**
 * One-time migration: rename per-device dismiss keys
 *   `announcement-${slug}-${id}-${updatedAt}` → `notification-${slug}-${id}-${updatedAt}`
 * so returning visitors don't see all rows as unread after the rename. Idempotent —
 * guarded by `notification-migration-v1=done` set once per device.
 */
function runDismissKeyMigration() {
  try {
    if (localStorage.getItem(MIGRATION_FLAG) === "done") return;
    const toMigrate: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("announcement-")) toMigrate.push(key);
    }
    for (const oldKey of toMigrate) {
      const newKey = "notification-" + oldKey.slice("announcement-".length);
      const value = localStorage.getItem(oldKey);
      if (value !== null && localStorage.getItem(newKey) === null) {
        localStorage.setItem(newKey, value);
      }
      localStorage.removeItem(oldKey);
    }
    localStorage.setItem(MIGRATION_FLAG, "done");
  } catch {
    // localStorage unavailable — skip silently; flag never set, retried next mount
  }
}

export function getDismissKey(n: VisitorNotification, mapSlug: string): string {
  return `notification-${mapSlug}-${n.id}-${n.updatedAt}`;
}

function computeUnread(notifications: VisitorNotification[], mapSlug: string): number {
  let count = 0;
  for (const n of notifications) {
    try {
      if (localStorage.getItem(getDismissKey(n, mapSlug)) !== "1") count++;
    } catch {
      count++;
    }
  }
  return count;
}

/**
 * Returns count of undismissed notifications.
 * Reactive: recomputes when notifications change, on cross-tab storage writes,
 * and on tab visibility change (catches same-tab dismissals).
 */
export function useNotificationsUnread(
  notifications: VisitorNotification[],
  mapSlug: string,
): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    runDismissKeyMigration();
    setCount(computeUnread(notifications, mapSlug));

    const sync = () => setCount(computeUnread(notifications, mapSlug));
    const handleVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };

    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [notifications, mapSlug]);

  return count;
}
